/**
 * @fileoverview Canvas side-rail panel that inventories every Pass/Fail-
 * enabled or already-violation-linked field across a template and lets
 * admins resolve missing or stale bindings without visiting each field.
 * @module template-builder
 *
 * Responsibilities:
 * - Compute the violation inventory via buildViolationInventory.
 * - Render filter chips (All / Unlinked / Linked / Stale) + search.
 * - Render ViolationInventoryCard per entry with Link/Change/Unlink/Fix.
 * - Surface "Fix all stale" when stale entries exist.
 * - Emit field-reveal and violation-link actions upward.
 *
 * Dependencies:
 * - ../../lib/buildViolationInventory
 * - ./ViolationInventoryCard
 * - ./ViolationPickerPopover
 *
 * Architecture:
 * - Reads the active code set data from host-provided arrays.
 * - Writes bind/unbind via the `onChangeViolationMapping` callback;
 *   persistence (Dexie + sync) is the host's responsibility.
 */
import { useMemo, useState } from 'react';
import { Search, X, Wrench } from 'lucide-react';
import type { TemplateBuilderTemplate, TemplateBuilderForm, ViolationMapping } from '../../types/templateTypes';
import type { ViolationIndexEntry } from '../../types/violationCodeSet';
import { buildViolationInventory, suggestStaleReplacement, type ViolationInventoryEntry } from '../../lib/buildViolationInventory';
import { ViolationInventoryCard } from './ViolationInventoryCard';
import { ViolationPickerPopover } from './ViolationPickerPopover';

type FilterId = 'all' | 'unlinked' | 'linked' | 'stale';

export interface CanvasViolationsPanelProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
  /** Active default code set id (from host). Used for staleness checks. */
  activeCodeSetId?: string;
  /** Violation index entries for the active set (title + code resolution). */
  activeCodeEntries?: ViolationIndexEntry[];
  /** Optional Dexie instance so the popover can embed ViolationPicker. */
  dexieDb?: any;
  /** Close the panel (collapses the side-rail). */
  onClose: () => void;
  /** Host writes the new mapping (Dexie + sync). `null` means unlink. */
  onChangeViolationMapping: (params: {
    formId: string;
    fieldId: string;
    mapping: ViolationMapping | null;
  }) => void;
  /** Scroll canvas to the field and select it (opens Properties panel). */
  onRevealField: (formId: string, sectionId: string | undefined, fieldId: string) => void;
}

/**
 * @component CanvasViolationsPanel
 *
 * Data Source:
 * - local (receives template + forms from canvas; code entries from host).
 *
 * Constraints:
 * - No direct Dexie writes. All mutations go through `onChangeViolationMapping`.
 */
export function CanvasViolationsPanel({
  template,
  forms,
  activeCodeSetId,
  activeCodeEntries,
  dexieDb,
  onClose,
  onChangeViolationMapping,
  onRevealField,
}: CanvasViolationsPanelProps) {
  const inventory = useMemo(
    () => buildViolationInventory(template, forms, { activeCodeSetId, activeCodeEntries }),
    [template, forms, activeCodeSetId, activeCodeEntries],
  );

  const [filter, setFilter] = useState<FilterId>('all');
  const [search, setSearch] = useState('');
  const [pickerTarget, setPickerTarget] = useState<ViolationInventoryEntry | null>(null);
  const [detailEntry, setDetailEntry] = useState<ViolationInventoryEntry | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return inventory.filter((entry) => {
      if (filter === 'unlinked' && entry.status !== 'unlinked') return false;
      if (filter === 'linked' && entry.status !== 'linked') return false;
      if (filter === 'stale' && entry.status !== 'stale') return false;
      if (!q) return true;
      const hay = [
        entry.fieldLabel,
        entry.linkedViolationCode ?? '',
        entry.linkedViolationTitle ?? '',
        entry.formName,
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [inventory, filter, search]);

  const counts = useMemo(() => ({
    all: inventory.length,
    linked: inventory.filter((e) => e.status === 'linked').length,
    unlinked: inventory.filter((e) => e.status === 'unlinked').length,
    stale: inventory.filter((e) => e.status === 'stale').length,
  }), [inventory]);

  const staleEntries = useMemo(() => inventory.filter((e) => e.status === 'stale'), [inventory]);

  const handlePickerSelect = (selection: {
    code: string;
    title: string;
    codeSetId: string;
    codeReference?: string;
    contentSnippet?: string;
    displayText?: string;
    bodyText?: string;
  }) => {
    if (!pickerTarget) return;
    const mapping: ViolationMapping = {
      codeSetId: selection.codeSetId,
      code: selection.code,
      title: selection.title,
      contentSnippet: selection.contentSnippet || selection.displayText || selection.bodyText,
      codeReference: selection.codeReference,
      trigger: { field: pickerTarget.fieldId, op: 'equals', value: true },
      requireNotes: false,
      requirePhotos: false,
      allowMultipleInstances: false,
    };
    onChangeViolationMapping({
      formId: pickerTarget.formId,
      fieldId: pickerTarget.fieldId,
      mapping,
    });
    setPickerTarget(null);
  };

  const handleFixAllStale = () => {
    if (!activeCodeEntries || activeCodeEntries.length === 0) return;
    for (const entry of staleEntries) {
      const replacement = suggestStaleReplacement(entry, activeCodeEntries);
      if (!replacement) continue;
      const mapping: ViolationMapping = {
        codeSetId: replacement.codeSetId,
        code: replacement.code,
        title: replacement.title,
        contentSnippet: replacement.contentSnippet,
        codeReference: replacement.codeReference,
        trigger: { field: entry.fieldId, op: 'equals', value: true },
        requireNotes: false,
        requirePhotos: false,
        allowMultipleInstances: false,
      };
      onChangeViolationMapping({ formId: entry.formId, fieldId: entry.fieldId, mapping });
    }
  };

  const totalLinkable = counts.linked + counts.unlinked + counts.stale;
  const headerBadge = `${counts.linked} of ${totalLinkable} linked`;

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 shrink-0">
        <div className="min-w-0">
          <p className="text-xs font-semibold text-gray-700">Violations</p>
          <p className="text-[10px] text-gray-500">{headerBadge}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label="Close violations panel"
        >
          <X size={14} />
        </button>
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-gray-100 shrink-0 text-[11px]">
        {(['all', 'unlinked', 'linked', 'stale'] as FilterId[]).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFilter(f)}
            className={`px-2 py-0.5 rounded-full border transition-colors ${
              filter === f
                ? 'text-white border-(--brand-primary)'
                : 'text-gray-600 border-gray-300'
            }`}
            style={{ backgroundColor: filter === f ? 'var(--brand-primary)' : undefined }}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
            <span className="ml-1 text-[10px] opacity-75">{counts[f]}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-gray-100 shrink-0">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search field label or code…"
            className="w-full pl-7 pr-2 py-1 text-xs border border-gray-200 rounded-sm focus:outline-hidden focus:ring-1 focus:ring-(--brand-primary)"
            aria-label="Filter violations"
          />
        </div>
      </div>

      {/* Fix-all-stale action bar */}
      {staleEntries.length > 0 && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b bg-red-50 border-red-100 shrink-0">
          <span className="text-[11px] text-red-700">
            {staleEntries.length} stale binding{staleEntries.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={handleFixAllStale}
            disabled={!activeCodeEntries || activeCodeEntries.length === 0}
            className="inline-flex items-center gap-1 text-[11px] font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed rounded-sm px-2 py-0.5 transition-colors"
          >
            <Wrench size={10} /> Fix all stale
          </button>
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {inventory.length === 0 ? (
          <div className="p-4 text-xs text-gray-500">
            No Pass/Fail fields in this template. Enable Pass/Fail on a field to start tracking violations.
          </div>
        ) : filtered.length === 0 ? (
          counts.unlinked === 0 && counts.stale === 0 && filter === 'all' ? (
            <div className="p-4 text-xs text-emerald-600">All violations linked ✓</div>
          ) : (
            <div className="p-4 text-xs text-gray-500">No entries match the current filter.</div>
          )
        ) : (
          <ul className="divide-y divide-gray-100">
            {filtered.map((entry) => (
              <ViolationInventoryCard
                key={`${entry.formId}:${entry.fieldId}`}
                entry={entry}
                onLink={() => setPickerTarget(entry)}
                onChange={() => setPickerTarget(entry)}
                onUnlink={() => onChangeViolationMapping({ formId: entry.formId, fieldId: entry.fieldId, mapping: null })}
                onFix={() => setPickerTarget(entry)}
                onShow={() => setDetailEntry(entry)}
                onReveal={() => onRevealField(entry.formId, entry.sectionId, entry.fieldId)}
              />
            ))}
          </ul>
        )}
      </div>

      <ViolationPickerPopover
        open={!!pickerTarget}
        onClose={() => setPickerTarget(null)}
        onSelect={(sel) => handlePickerSelect(sel)}
        codeSetIds={activeCodeSetId ? [activeCodeSetId] : undefined}
        dexieDb={dexieDb}
      />
      {detailEntry ? (
        <div
          className="fixed inset-0 z-50 bg-gray-900/30 flex items-center justify-center p-4"
          onClick={() => setDetailEntry(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Violation details"
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 rounded-t-lg">
              <span className="text-sm font-semibold text-gray-800">Violation details</span>
              <button
                type="button"
                onClick={() => setDetailEntry(null)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close violation details"
              >
                <X size={16} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Code</div>
                <div className="mt-1 font-mono text-sm font-bold text-gray-900">{detailEntry.linkedViolationCode}</div>
              </div>
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Description</div>
                <div className="mt-1 text-sm text-gray-800">{detailEntry.linkedViolationTitle || 'Description unavailable'}</div>
              </div>
              {detailEntry.linkedViolationText ? (
                <div>
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Text</div>
                  <div className="mt-1 max-h-36 overflow-y-auto rounded-sm border border-gray-200 bg-gray-50 p-2 text-xs leading-5 text-gray-700">
                    {detailEntry.linkedViolationText}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CanvasViolationsPanel;
